// Live previews for Admin > Content: the Discord post (mirrors src/server/discord/format.ts) and the website card.
import { cn } from "../../ui/index.tsx";
import { LogoMark } from "../../ui/logo.tsx";
import { Markdown } from "../../ui/markdown.tsx";
import type { AnnouncementLevel } from "../../../shared/types.ts";

// Keep in step with COLOUR / KICKER in src/server/discord/format.ts.
const COLOUR: Record<AnnouncementLevel, string> = { info: "#ff3d4f", warning: "#ffb020", critical: "#ff4d4d" };
const KICKER: Record<AnnouncementLevel, string> = { info: "ANNOUNCEMENT", warning: "NOTICE", critical: "IMPORTANT" };

export interface PreviewProps {
  title: string;
  message: string;
  level: AnnouncementLevel;
  imageUrl?: string;
  everyone: boolean;
  poll?: { question: string; answers: string[]; hours: number } | null;
}

export function DiscordPreview({ title, message, level, imageUrl, everyone, poll }: PreviewProps) {
  const now = new Date().toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" });
  return (
    <div className="rounded-lg bg-[#313338] p-4 font-[system-ui] text-[15px] leading-[1.375] text-[#dbdee1] shadow-inner">
      <div className="mb-3 flex items-center gap-2 text-[13px] text-[#949ba4]">
        <span className="text-lg leading-none">#</span><span className="font-semibold text-[#f2f3f5]">📢-announcements</span>
      </div>
      <div className={cn("-mx-4 flex gap-4 px-4 py-1", everyone && "border-l-2 border-[#f0b232] bg-[#f0b232]/10")}>
        <LogoMark animated={false} className="mt-0.5 size-10 shrink-0 rounded-full" />
        <div className="min-w-0 flex-1">
          <div className="flex items-baseline gap-1.5">
            <span className="font-medium text-white">ExoWare</span>
            <span className="rounded bg-[#5865f2] px-1 text-[10px] font-semibold leading-[15px] text-white">✓ APP</span>
            <span className="text-xs text-[#949ba4]">Today at {now}</span>
          </div>
          {everyone && <div><span className="rounded bg-[#5865f2]/30 px-0.5 font-medium text-[#c9cdfb]">@everyone</span></div>}
          <div className="mt-1 flex max-w-[520px] overflow-hidden rounded bg-[#2b2d31]">
            <div className="w-1 shrink-0" style={{ background: COLOUR[level] }} />
            <div className="min-w-0 flex-1 px-4 pb-4 pt-2">
              <div className="mt-2 flex items-center gap-2 text-[13px] font-semibold text-white">
                <LogoMark animated={false} className="size-6 rounded-full" />EXOWARE  ·  {KICKER[level]}
              </div>
              <div className="mt-2 font-semibold text-[#00a8fc]">{title || <span className="text-[#949ba4]">Title</span>}</div>
              <div className="mt-2 text-[14px]">{message ? <Markdown tone="discord" text={message} /> : <span className="text-[#949ba4]">Your message appears here.</span>}</div>
              {imageUrl && <img src={imageUrl} alt="" className="mt-4 max-h-72 w-full rounded object-cover" />}
              <div className="mt-2 flex items-center gap-2 text-[12px] text-[#949ba4]">
                <LogoMark animated={false} className="size-5 rounded-full" />exoware.app  ·  ExoWare  •  Today at {now}
              </div>
            </div>
          </div>
          {poll && (
            <div className="mt-2 max-w-[440px] rounded-lg border border-[#3f4147] bg-[#2b2d31] p-4">
              <div className="font-semibold text-white">{poll.question || "Poll question"}</div>
              <div className="mb-3 text-xs text-[#949ba4]">Select one or more answers</div>
              <div className="space-y-2">
                {(poll.answers.filter(Boolean).length ? poll.answers.filter(Boolean) : ["Answer 1", "Answer 2"]).map((a, i) => (
                  <div key={i} className="flex items-center justify-between rounded-md border border-[#3f4147] bg-[#1e1f22] px-3 py-2 text-sm">
                    <span>{a}</span><span className="size-4 rounded border border-[#80848e]" />
                  </div>
                ))}
              </div>
              <div className="mt-3 text-xs text-[#949ba4]">0 votes · {poll.hours >= 24 ? `${Math.round(poll.hours / 24)}d` : `${poll.hours}h`} left</div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

/** How the announcement appears to signed-in users on exoware.app. */
export function SitePreview({ title, message, level }: Pick<PreviewProps, "title" | "message" | "level">) {
  const tone = { info: "border-info/40 bg-info/5", warning: "border-warn/40 bg-warn/5", critical: "border-bad/40 bg-bad/5" }[level];
  return (
    <div className={cn("rounded-[var(--radius-card)] border-l-2 border p-4", tone)}>
      <div className="kicker mb-1">{level}</div>
      <div className="font-semibold">{title || <span className="text-faint">Title</span>}</div>
      {message ? <Markdown text={message} className="mt-1 text-sm text-muted" /> : <p className="mt-1 text-sm text-muted">Your message appears here.</p>}
    </div>
  );
}
